import React, {useEffect, useState} from "react";
import { Button, Form } from "antd";
import { ServiceInfo } from "../entities/ServiceInfo";
import {BuildType, PackageTemplate} from "../entities/PackageTemplate";
import { DEPLOY_ENV } from "../utils/config";
import { toast } from "react-toastify";
import PackageComponent from "../components/PackageComponent";
import {AxiosProgressEvent} from "axios";
import PackageService from "../http/PackageService";
import {PackageTemplateService} from "../http/PackageTemplateService";

interface PackagePageProps {
    service: ServiceInfo;
}

const PackagePage: React.FC<PackagePageProps> = ({ service }) => {
    const [form] = Form.useForm();
    const [template, setTemplate] = useState<PackageTemplate | null>(null);
    const [isNew, setIsNew] = useState<boolean>(true);
    const [file, setFile] = useState<File | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [buildType, setBuildType] = useState<BuildType>("LOCAL");
    const [uploadProgress, setUploadProgress] = useState<number>(0);

    useEffect(() => {
        setFile(null);
        setLoading(false);
        setUploadProgress(0);
    }, [service.appKey]);

    const onProgress = (progressEvent: AxiosProgressEvent) => {
        const { loaded, total } = progressEvent;
        const percent = total ? Math.round((loaded * 100) / total) : 0;
        setUploadProgress(percent);
    };

    const saveTemplate = async () => {
        try {
            const params = await form.validateFields();
            delete params.buildType;
            const request: PackageTemplate = {
                appKey: service.appKey,
                env: DEPLOY_ENV,
                buildType: buildType,
                param: params,
            } as PackageTemplate;
            if (isNew) {
                await PackageTemplateService.createTemplate(request);
                toast.success("创建成功！");
            } else {
                await PackageTemplateService.updateTemplate(request);
                toast.success("更新成功！");
            }
        }
        catch (error) {
            console.error("表单验证失败", error);
        }
    }

    const buildPackage = async () => {
        try {
            const params = await form.validateFields();
            delete params.buildType;
            const request = {
                appKey: service.appKey,
                env: DEPLOY_ENV,
                buildType: buildType,
                param: params,
                packageName: params.packageName,
            };
            if (buildType === "LOCAL") {
                if (!file) {
                    toast.error("请选择文件！");
                    return;
                }
                setLoading(true);
                const response = await PackageService.uploadPackage(request, file, onProgress);
                setLoading(false);
                if (response.isFailed()) {
                    return;
                }
                toast.success("上传完成！");
            } else if (buildType === "REMOTE") {
                setLoading(true);
                const response = await PackageService.buildPackage(request);
                setLoading(false);
                if (response.isFailed()) {
                    return;
                }
                toast.success("构建开始");
                const id = response.data.id;
                window.open(`/detail/package?id=${id}`, "_blank");
            }
        } catch (error) {
            console.error("表单验证失败", error);
            setLoading(false);
        }
    };

    return (
        <>
            <Form
                form={form}
                layout="horizontal"
                onValuesChange={(changedValues, allValues) => {
                    if (changedValues.buildType !== undefined) {
                        setBuildType(changedValues.buildType);
                    }
                }}
                labelCol={{ style: { width: "150px" } }}>
                <PackageComponent
                    service={service}
                    form={form}
                    templateLoaded={(t) => {
                        if (t){
                            setTemplate(t);
                            setIsNew(false);
                        }
                    }}
                    filePicked={setFile}
                />
            </Form>
            <div className="fixed-footer">
                <Button type="default" onClick={saveTemplate}>
                    {isNew ? "保存模板" : "更新模板"}
                </Button>
                <Button
                    type="primary"
                    onClick={buildPackage}
                    disabled={loading}
                    loading={loading}>
                    {buildType === "LOCAL"
                        ? (loading ? `上传 (${uploadProgress}%)` : "上传")
                        : "构建"}
                </Button>
            </div>
        </>
    );
};

export default PackagePage;
